import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  BankView, DryRunResult, EntityKeyView, FeatureFlagView, FiscalYearView,
  GenerateCalendarResult, NotificationChannelView, TestEmailResult, NotificationTemplateView, PayrollCalendarView,
  SequenceView, WorkflowDefinitionView, WorkflowPeopleView,
} from '../types'

const BASE = '/v1/settings'

export function useFeatureFlags() {
  return useQuery({
    queryKey: ['settings', 'feature-flags'],
    queryFn: async () => (await apiClient.get<FeatureFlagView[]>(`${BASE}/feature-flags`)).data,
  })
}

export function useNotificationTemplates() {
  return useQuery({
    queryKey: ['settings', 'notification-templates'],
    queryFn: async () =>
      (await apiClient.get<NotificationTemplateView[]>(`${BASE}/notification-templates`)).data,
  })
}

export function useNotificationChannels() {
  return useQuery({
    queryKey: ['settings', 'notification-channels'],
    queryFn: async () =>
      (await apiClient.get<NotificationChannelView[]>(`${BASE}/notification-channels`)).data,
  })
}

export function useFiscalYears() {
  return useQuery({
    queryKey: ['settings', 'fiscal-years'],
    queryFn: async () => (await apiClient.get<FiscalYearView[]>(`${BASE}/fiscal-years`)).data,
  })
}

export function usePayrollCalendar() {
  return useQuery({
    queryKey: ['settings', 'payroll-calendar'],
    queryFn: async () =>
      (await apiClient.get<PayrollCalendarView[]>(`${BASE}/payroll-calendar`)).data,
  })
}

export function useBanks() {
  return useQuery({
    queryKey: ['settings', 'banks'],
    queryFn: async () => (await apiClient.get<BankView[]>(`${BASE}/banks`)).data,
  })
}

export function useSequences() {
  return useQuery({
    queryKey: ['settings', 'sequences'],
    queryFn: async () => (await apiClient.get<SequenceView[]>(`${BASE}/sequences`)).data,
  })
}

export function useWorkflowEntityKeys() {
  return useQuery({
    queryKey: ['settings', 'workflow-entity-keys'],
    queryFn: async () =>
      (await apiClient.get<EntityKeyView[]>(`${BASE}/workflows/entity-keys`)).data,
  })
}

/** The roles and people a workflow step can name. */
export function useWorkflowPeople() {
  return useQuery({
    queryKey: ['settings', 'workflow-people'],
    queryFn: async () =>
      (await apiClient.get<WorkflowPeopleView>(`${BASE}/workflows/people`)).data,
    staleTime: 5 * 60_000,
  })
}

export function useWorkflowDefinitions(entityKey?: string) {
  return useQuery({
    queryKey: ['settings', 'workflows', entityKey ?? 'all'],
    queryFn: async () =>
      (await apiClient.get<WorkflowDefinitionView[]>(`${BASE}/workflows`, {
        params: { entityKey: entityKey || undefined },
      })).data,
  })
}

export function useSettingsMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['settings'] })

  return {
    /**
     * Currency, symbol, timezone and formats.
     *
     * <p>The endpoint has always accepted these; there was no screen that sent them, which is
     * why a UAE company could sit on the schema's default symbol — the Bangladeshi taka from
     * the original reference design — with no way to correct it. Invalidates the whole
     * settings key, so every screen reformats its money without a reload.
     */
    saveLocalisation: useMutation({
      mutationFn: async (body: {
        defaultCurrency: string
        currencySymbol: string
        timezone: string
        dateFormat: string
        timeFormat: string
        weekStart: number
        fiscalYearStart: string
        defaultLocale: string
        primaryColor: string
      }) => (await apiClient.put<unknown>(`${BASE}/localization`, body)).data,
      onSuccess: invalidate,
    }),
    setFlag: useMutation({
      mutationFn: async (body: { key: string; enabled: boolean; note?: string | null }) =>
        (await apiClient.put<FeatureFlagView[]>(`${BASE}/feature-flags`, body)).data,
      onSuccess: invalidate,
    }),
    saveTemplate: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<NotificationTemplateView>(
            `${BASE}/notification-templates/${id}`, body)).data
          : (await apiClient.post<NotificationTemplateView>(
            `${BASE}/notification-templates`, body)).data,
      onSuccess: invalidate,
    }),
    /**
     * Sends a test email through the saved settings, now. Answers within the server's
     * ten-second timeout, with what went wrong in plain words when it did.
     */
    testEmail: useMutation({
      mutationFn: async (to: string | null) =>
        (await apiClient.post<TestEmailResult>(
          `${BASE}/notification-channels/email/test`, { to })).data,
    }),
    saveChannel: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.put<NotificationChannelView>(`${BASE}/notification-channels`, body)).data,
      onSuccess: invalidate,
    }),
    saveFiscalYear: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<FiscalYearView>(`${BASE}/fiscal-years/${id}`, body)).data
          : (await apiClient.post<FiscalYearView>(`${BASE}/fiscal-years`, body)).data,
      onSuccess: invalidate,
    }),
    closeFiscalYear: useMutation({
      mutationFn: async ({ id, closed }: { id: string; closed: boolean }) =>
        (await apiClient.post<FiscalYearView>(`${BASE}/fiscal-years/${id}/close`, null,
          { params: { closed } })).data,
      onSuccess: invalidate,
    }),
    generateCalendar: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<GenerateCalendarResult>(`${BASE}/payroll-calendar/generate`, body)).data,
      onSuccess: invalidate,
    }),
    saveBank: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<BankView>(`${BASE}/banks/${id}`, body)).data
          : (await apiClient.post<BankView>(`${BASE}/banks`, body)).data,
      onSuccess: invalidate,
    }),
    saveSequence: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<SequenceView>(`${BASE}/sequences/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    saveWorkflow: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<WorkflowDefinitionView>(`${BASE}/workflows/${id}`, body)).data
          : (await apiClient.post<WorkflowDefinitionView>(`${BASE}/workflows`, body)).data,
      onSuccess: invalidate,
    }),
    activateWorkflow: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<WorkflowDefinitionView>(`${BASE}/workflows/${id}/activate`)).data,
      onSuccess: invalidate,
    }),
    deactivateWorkflow: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<WorkflowDefinitionView>(`${BASE}/workflows/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
    dryRun: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.post<DryRunResult>(`${BASE}/workflows/${id}/dry-run`, body)).data,
    }),
  }
}

/** How many records each onboarding step has loaded — counts only, nobody named. */
export interface DataMigrationSummary {
  employees: number
  bankAccounts: number
  documents: number
  employeesWithPay: number
  leaveBalances: number
  airTicketHistory: number
  openingProvisions: number
  migratedLoans: number
}

export function useDataMigrationSummary(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'data-migration', 'summary'],
    enabled,
    queryFn: async () =>
      (await apiClient.get<DataMigrationSummary>(`${BASE}/data-migration/summary`)).data,
  })
}
